import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { fetchStores } from '../api/stores';
import {
  fetchDashboard,
  fetchSalesReport,
  fetchInventoryReport,
  fetchDocumentsReport
} from '../api/reports';
import { getErrorMessage } from '../api/client';

function toCSV(rows, columns) {
  const header = columns.join(',');
  const body = rows.map((r) => columns.map((c) => (r[c] != null ? String(r[c]).replace(/"/g, '""') : '')).map((v) => `"${v}"`).join(',')).join('\n');
  return header + '\n' + body;
}

export default function ReportsPage() {
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [sales, setSales] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [tab, setTab] = useState(tabFromUrl === 'sales' ? 'sales' : 'dashboard');
  const [docSearch, setDocSearch] = useState('');
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStores()
      .then((list) => {
        const arr = Array.isArray(list) ? list : [];
        setStores(arr);
        if (arr.length && !storeId) setStoreId(arr[0]._id || arr[0].id || '');
      })
      .catch(() => setStores([]));
  }, [storeId]);

  const loadDashboard = useCallback(async () => {
    if (!auth.token) return;
    setError('');
    setLoading(true);
    try {
      const d = await fetchDashboard(auth.token, storeId ? { storeId } : {});
      setDashboard(d);
    } catch (err) {
      if (err?.response?.status === 401) auth.logout('Session expired');
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [auth, storeId]);

  const loadSales = useCallback(async () => {
    if (!auth.token) return;
    setError('');
    setLoading(true);
    try {
      let f = from;
      let t = to;
      if (f && t && new Date(f) > new Date(t)) {
        [f, t] = [t, f]; // swap if inverted
      }
      const params = { from: f, to: t };
      if (storeId) params.storeId = storeId;
      const s = await fetchSalesReport(auth.token, params);
      setSales(s);
    } catch (err) {
      if (err?.response?.status === 401) auth.logout('Session expired');
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [auth, from, to, storeId]);

  const loadInventory = useCallback(async () => {
    if (!auth.token) return;
    setError('');
    setLoading(true);
    try {
      const params = storeId ? { storeId } : {};
      const inv = await fetchInventoryReport(auth.token, params);
      setInventory(inv);
    } catch (err) {
      if (err?.response?.status === 401) auth.logout('Session expired');
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [auth, storeId]);

  const loadDocuments = useCallback(async () => {
    if (!auth.token) return;
    setError('');
    setLoading(true);
    try {
      const params = { from, to };
      if (storeId) params.storeId = storeId;
      const doc = await fetchDocumentsReport(auth.token, params);
      setDocuments(doc);
    } catch (err) {
      if (err?.response?.status === 401) auth.logout('Session expired');
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [auth, from, to, storeId]);

  useEffect(() => {
    if (tab === 'dashboard') loadDashboard();
  }, [tab, storeId, loadDashboard]);

  useEffect(() => {
    if (tab === 'sales') loadSales();
  }, [tab, from, to, storeId, loadSales]);

  useEffect(() => {
    if (tab === 'inventory') loadInventory();
  }, [tab, storeId, loadInventory]);

  useEffect(() => {
    if (tab === 'documents') loadDocuments();
  }, [tab, from, to, storeId, loadDocuments]);

  const exportSalesCSV = () => {
    if (!sales?.orders?.length) return;
    const rows = sales.orders.map((o) => ({
      orderId: o._id,
      date: o.createdAt,
      total: o.totalAmount,
      customerName: o.customerName
    }));
    const csv = toCSV(rows, ['orderId', 'date', 'total', 'customerName']);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-${from}-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportInventoryCSV = () => {
    if (!inventory?.products?.length) return;
    const csv = toCSV(inventory.products, ['name', 'sku', 'category', 'quantityOnHand', 'cost', 'value', 'lowStock']);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!auth.token) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Log in to view reports.</p>
          <Link to="/" className="text-indigo-600 underline">Back to store</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-indigo-600 hover:underline text-sm">← Store</Link>
            <h1 className="text-xl font-bold">Reports</h1>
          </div>
          <select
            value={storeId}
            onChange={(e) => setStoreId(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
            aria-label="Store"
          >
            <option value="">All stores</option>
            {stores.map((s) => (
              <option key={s._id || s.id} value={s._id || s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex gap-2 mb-4 border-b">
          {['dashboard', 'sales', 'inventory', 'documents'].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-600'}`}
            >
              {t === 'dashboard' ? 'Dashboard' : t === 'sales' ? 'Sales' : t === 'inventory' ? 'Inventory' : 'Documents'}
            </button>
          ))}
        </div>

        {error && <p className="text-red-600 mb-4">{error}</p>}
        {loading && <p className="text-gray-500">Loading...</p>}

        {tab === 'dashboard' && dashboard && !loading && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow p-4">
                <p className="text-sm text-gray-500">Today&apos;s sales</p>
                <p className="text-2xl font-bold">P{Number(dashboard.todaySales || 0).toFixed(2)}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <p className="text-sm text-gray-500">Today&apos;s orders</p>
                <p className="text-2xl font-bold">{dashboard.todayOrderCount || 0}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <p className="text-sm text-gray-500">Low stock items</p>
                <p className="text-2xl font-bold text-amber-600">{dashboard.lowStockCount || 0}</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-2">Top products today</h3>
              {dashboard.topProducts?.length ? (
                <ul className="space-y-1">
                  {dashboard.topProducts.map((p, i) => (
                    <li key={p.productId || i}>{p.name} — {p.qty} sold</li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500">No sales today</p>
              )}
            </div>
          </div>
        )}

        {tab === 'sales' && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border rounded px-2 py-1" aria-label="From date" />
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border rounded px-2 py-1" aria-label="To date" />
              <input type="text" value={docSearch} onChange={(e) => setDocSearch(e.target.value)} placeholder="Search by document/order number" className="border rounded px-2 py-1 min-w-[180px]" />
              <button type="button" onClick={loadSales} className="bg-indigo-600 text-white px-3 py-1 rounded text-sm">Apply</button>
              {sales?.orders?.length > 0 && (
                <button type="button" onClick={exportSalesCSV} className="border rounded px-3 py-1 text-sm">Export CSV</button>
              )}
            </div>
            {sales && (
              <>
                <p className="text-sm text-gray-600">Total sales: P{Number(sales.totalSales || 0).toFixed(2)} ({sales.orderCount} orders)</p>
                <div className="bg-white rounded shadow overflow-auto max-h-96">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-2">Order ID</th>
                        <th className="text-left p-2">Date</th>
                        <th className="text-right p-2">Total</th>
                        <th className="text-right p-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(sales.orders || [])
                        .filter((o) => !docSearch.trim() || (o._id && String(o._id).toLowerCase().includes(docSearch.toLowerCase())))
                        .map((o) => (
                        <tr key={o._id} className="border-b hover:bg-gray-50">
                          <td className="p-2">{o._id}</td>
                          <td className="p-2">{o.createdAt ? new Date(o.createdAt).toLocaleString() : ''}</td>
                          <td className="p-2 text-right">P{Number(o.totalAmount || 0).toFixed(2)}</td>
                          <td className="p-2 text-right">
                            <button type="button" onClick={() => { const w = window.open('', '_blank'); w.document.write(`<html><body><h2>Order ${o._id}</h2><p>Date: ${o.createdAt ? new Date(o.createdAt).toLocaleString() : ''}</p><p>Total: P${Number(o.totalAmount || 0).toFixed(2)}</p><pre>${JSON.stringify(o, null, 2)}</pre></body></html>`); w.document.close(); w.print(); w.close(); }} className="text-indigo-600 hover:underline text-xs mr-2">Print</button>
                            <button type="button" onClick={() => { const w = window.open('', '_blank'); w.document.write(`<html><body><h2>Invoice - Order ${o._id}</h2><p>Date: ${o.createdAt ? new Date(o.createdAt).toLocaleString() : ''}</p><p>Customer: ${o.customerName || ''}</p><p>Total: P${Number(o.totalAmount || 0).toFixed(2)}</p></body></html>`); w.document.close(); w.print(); w.close(); }} className="text-indigo-600 hover:underline text-xs">Save as PDF</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'inventory' && (
          <div className="space-y-4">
            {inventory && (
              <>
                <div className="flex gap-2">
                  <p className="text-sm">Total value: P{Number(inventory.totalValue || 0).toFixed(2)}</p>
                  <p className="text-sm text-amber-600">Low stock: {inventory.lowStockCount} items</p>
                  <button type="button" onClick={exportInventoryCSV} className="border rounded px-3 py-1 text-sm ml-auto">Export CSV</button>
                </div>
                <div className="bg-white rounded shadow overflow-auto max-h-96">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-2">Name</th>
                        <th className="text-left p-2">SKU</th>
                        <th className="text-right p-2">QOH</th>
                        <th className="text-right p-2">Value</th>
                        <th className="text-left p-2">Low</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(inventory.products || []).map((p) => (
                        <tr key={p._id} className="border-b">
                          <td className="p-2">{p.name}</td>
                          <td className="p-2">{p.sku || '-'}</td>
                          <td className="p-2 text-right">{p.quantityOnHand}</td>
                          <td className="p-2 text-right">P{Number(p.value || 0).toFixed(2)}</td>
                          <td className="p-2">{p.lowStock ? 'Yes' : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'documents' && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border rounded px-2 py-1" aria-label="From date" />
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border rounded px-2 py-1" aria-label="To date" />
              <button type="button" onClick={loadDocuments} className="bg-indigo-600 text-white px-3 py-1 rounded text-sm">Apply</button>
            </div>
            {documents && (
              <p className="text-sm text-gray-600">{documents.count} documents</p>
            )}
            <div className="bg-white rounded shadow overflow-auto max-h-96">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-2">Type</th>
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(documents?.documents || []).map((d) => (
                    <tr key={d._id} className="border-b">
                      <td className="p-2">{d.type}</td>
                      <td className="p-2">{d.date ? new Date(d.date).toLocaleDateString() : ''}</td>
                      <td className="p-2">{d.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
